import "./index.css";
import { memo } from "react";
import { FaPlus, FaRobot } from "react-icons/fa";
import { Handle } from "reactflow";

import { ValidatorFn } from "../../../helpers/validationConnector";

import { NodeHeader } from "../../Elements/NodeHeader";
import { NodeRef } from "../../Elements/NodeRef";

// Saida reservada: o backend segue por ela quando a conta bate o teto de
// creditos e o agente nao pode abrir conversa nova. Mesmo id do builder-backend.
export const AI_CONTINGENCY_HANDLE = "ai-contingency";

// Quantos badges mostrar antes de colapsar no "+N".
const MAX_VAR_BADGES = 4;
const MAX_TOOL_BADGES = 3;

// O objeto completo do agente (variables/tools/objective/datasets/...) vem na
// lista `data.agents`, injetada por Flow.js em cada nó. O nó em si só salva
// aiAgentId/aiAgentName/aiOutputs, então casamos por id p/ enriquecer o card.
function findAgent(data) {
	const id = data.aiAgentId;
	if (!id) return null;
	return (data.agents || []).find((a) => (a?.id || a?._id) === id) || null;
}

export default memo(
	({ data, ...rest }) => {
		const agent = findAgent(data);

		const agentLabel =
			data.aiAgentName ||
			agent?.name ||
			data.aiAgentId ||
			"Agente não selecionado";

		const objective = agent?.objective || "";
		const role = agent?.agent_role?.prompt_instructions || "";
		const style = agent?.speaking_style?.prompt_instructions || "";
		const isActive = agent?.status === "active";

		// Variáveis do agente (label + instructions). Fallback p/ as mapeadas no
		// nó quando o agente completo não está na lista (ex.: agente removido).
		const agentVars = (agent?.variables || []).filter((v) => v?.label);
		const varLabels = agentVars.length
			? agentVars.map((v) => v.label)
			: (data.aiInputVariables || []).map((v) => v.label).filter(Boolean);

		const tools = agent?.tools || [];
		const datasets = agent?.datasets || [];
		const rulesCount = (agent?.custom_rules || []).length;

		// Saídas = as salvas no nó (base das conexões existentes). Fallback p/ as
		// do agente completo.
		const outputs = data.aiOutputs?.length ? data.aiOutputs : agent?.outputs || [];

		return (
			<>
				<NodeRef nodeRef={data.ref} />
				<Handle
					type="target"
					position="left"
					isConnectable={true}
					isConnectableStart={false}
				/>

				<NodeHeader
					rest={rest}
					data={data}
					icon={<FaRobot />}
					text="Agente de IA"
				/>

				<div className={`aiAgentBody${agent ? " has-agent" : ""}`}>
					<div className="aiAgentIdentity">
						<div className="aiAgentName" title={agentLabel}>
							{agentLabel}
						</div>
						{agent?.status && (
							<span
								className={`aiAgentStatus ${isActive ? "is-active" : "is-inactive"}`}
							>
								{isActive ? "Ativo" : "Inativo"}
							</span>
						)}
					</div>

					{(role || style) && (
						<div
							className="aiAgentMeta"
							title={`${role}${role && style ? " · " : ""}${style}`}
						>
							{role && <span>{role}</span>}
							{role && style && <span className="aiAgentMetaSep">·</span>}
							{style && <span>{style}</span>}
						</div>
					)}

					{objective && (
						<div className="aiAgentSection">
							<div className="aiAgentSectionLabel">Objetivo</div>
							<div className="aiAgentObjective" title={objective}>
								{objective}
							</div>
						</div>
					)}

					{varLabels.length > 0 && (
						<div className="aiAgentSection">
							<div className="aiAgentSectionLabel">Variáveis de entrada</div>
							<BadgeList items={varLabels} max={MAX_VAR_BADGES} variant="var" />
						</div>
					)}

					{tools.length > 0 && (
						<div className="aiAgentSection">
							<div className="aiAgentSectionLabel">Ferramentas</div>
							<BadgeList
								items={tools.map((t) => t.name).filter(Boolean)}
								max={MAX_TOOL_BADGES}
								variant="tool"
							/>
						</div>
					)}

					{(datasets.length > 0 || rulesCount > 0) && (
						<div className="aiAgentFooterMeta">
							{datasets.length > 0 && (
								<span
									className="aiAgentMetaChip"
									title={datasets
										.map((d) => d.datasetId?.name)
										.filter(Boolean)
										.join(", ")}
								>
									{datasets.length} base{datasets.length > 1 ? "s" : ""} de
									conhecimento
								</span>
							)}
							{rulesCount > 0 && (
								<span className="aiAgentMetaChip">
									{rulesCount} regra{rulesCount > 1 ? "s" : ""}
								</span>
							)}
						</div>
					)}
				</div>

				<div className="aiAgentOutputsLabel">Saídas</div>
				<div className="questionButtons">
					{outputs.map((output) => (
						<Item
							key={output.name}
							title={output.name}
							id={output.name}
							tooltip={output.instructions}
						/>
					))}
					<Item
						title="Sem créditos"
						id={AI_CONTINGENCY_HANDLE}
						isContingency
						tooltip="Usada quando a conta atinge o teto de créditos de IA do mês"
					/>
				</div>
			</>
		);
	},
	(prevProps, nextProps) => {
		// Re-renderizar se qualquer uma dessas propriedades mudar
		return (
			prevProps.data.aiAgentId === nextProps.data.aiAgentId &&
			prevProps.data.aiAgentName === nextProps.data.aiAgentName &&
			prevProps.data.aiInputVariables?.length ===
				nextProps.data.aiInputVariables?.length &&
			prevProps.data.aiOutputs?.length === nextProps.data.aiOutputs?.length &&
			// a lista de agentes popula o overview; re-render quando ela chega/muda
			prevProps.data.agents === nextProps.data.agents &&
			prevProps.id === nextProps.id &&
			prevProps.selected === nextProps.selected
		);
	}
);

function BadgeList({ items, max, variant }) {
	const shown = items.slice(0, max);
	const extra = items.length - shown.length;
	return (
		<div className="aiAgentBadges">
			{shown.map((label, i) => (
				<span
					key={`${label}-${i}`}
					className={`aiAgentBadge aiAgentBadge--${variant}`}
					title={label}
				>
					{label}
				</span>
			))}
			{extra > 0 && (
				<span className="aiAgentBadge aiAgentBadge--more">+{extra}</span>
			)}
		</div>
	);
}

function Item({ title, id, isContingency, tooltip }) {
	return (
		<div className={isContingency ? "item-last" : ""} title={tooltip}>
			<div className="conditionalContent">{title}</div>
			<Handle
				type="source"
				position="right"
				id={id}
				className="handle"
				isConnectable={true}
				isValidConnection={ValidatorFn()}
			>
				<FaPlus size={8} style={{ pointerEvents: "none" }} />
			</Handle>
		</div>
	);
}
